import { Option, Value } from 'baseui/select'
import isEqual from 'lodash/isEqual'
import React, { useContext, useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { FormSavePrompt, InfoTooltip, InputWrapper, OverlaySpinner } from '../../../components'
import {
    toMenuOptions,
    WemoloAccordion,
    WemoloButton,
    WemoloDropdown,
    WemoloTypography
} from '../../../components/Wemolo'
import { SliderComponent } from '../../../components/Slider/Slider'
import { getUserUuidFromLocalStorage, useFormSaveStore } from '../../../utils'
import { StationContext } from '../StationContext'
import { TrajectoryConfigurationMode } from '../station_types'
import { FilterTool } from './FilterTool'
import { InlineTool } from './InlineTool'
import { TrajectoryConfigurationContext } from './TrajectoryConfigurationContext'
import { StationTrajectoryPanel } from './components'
import { delete_recursive } from './util_inline_tool/delete_recursive'
import { export_transformation_inline_tool } from './util_inline_tool/export_transformation_inline_tool'
import { insert_recursive } from './util_inline_tool/insert_recursive'
import { validate_polygon } from './util_inline_tool/validate_polygon'
import { ImageSelectionButtonGroup } from './components/ButtonGroup'
import styles from './TrajectoryConfiguration.module.css'

// Compare if two arrays are equal
const arraysAreEqual = (arr1: any, arr2: any) => {
    if (arr1.length !== arr2.length) {
        return false
    }
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i].id !== arr2[i].id || arr1[i].label !== arr2[i].label) {
            return false
        }
    }
    return true
}

const transformValue = (value: any) => {
    return {
        label: value,
        id: value
    }
}

export const TrajectoryConfiguration = () => {
    const stationContext = useContext(StationContext)
    const trajectoryConfigurationContext = useContext(TrajectoryConfigurationContext)
    const [optionsViewMode, setOptionsViewMode] = useState<any>({
        FilterTool: [],
        InlineTool: []
    })
    const [polygonExists, setpolygonExists] = useState<boolean>()
    const [optionsConfigurationMode, setOptionsConfigurationMode] = useState<any>()
    const setFormChanges = useFormSaveStore((state) => state.setFormChanges)
    const hasChanges = useFormSaveStore((state) => state.hasChanges)
    const forceUpdateDisabled =
        !!stationContext.deviceData?.config_force_update ||
        stationContext.deviceData?.config_current_timestamp === stationContext.deviceData?.config_target_timestamp
    const previousFilterDataRef = useRef(trajectoryConfigurationContext.filterData)

    useEffect(() => {
        Sentry.logger.info('vehicle_trajectory.viewed', {
            event: 'vehicle_trajectory_viewed',
            deviceUuid: stationContext.deviceData?.uuid,
            stationName: stationContext.selectedStation?.station_name,
            lotId: stationContext.getLotId(),
            userId: getUserUuidFromLocalStorage()
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (
            trajectoryConfigurationContext.filterData === null ||
            trajectoryConfigurationContext.filterData === undefined ||
            previousFilterDataRef === undefined ||
            !previousFilterDataRef.current.length
        ) {
            previousFilterDataRef.current = trajectoryConfigurationContext.filterData
            return
        }
        const arraysEqual = isEqual(previousFilterDataRef.current, trajectoryConfigurationContext.filterData)
        if (!arraysEqual) {
            setFormChanges(true)
        }
        previousFilterDataRef.current = trajectoryConfigurationContext.filterData
    }, [trajectoryConfigurationContext.filterData, setFormChanges])

    useEffect(() => {
        if (stationContext.deviceData?.uuid) {
            setOptionsConfigurationMode([
                {
                    label: TrajectoryConfigurationMode.FilterTool,
                    id: TrajectoryConfigurationMode.FilterTool
                },
                {
                    label: TrajectoryConfigurationMode.InlineTool,
                    id: TrajectoryConfigurationMode.InlineTool
                }
            ])
            stationContext.setConfigurationMode(TrajectoryConfigurationMode.InlineTool as TrajectoryConfigurationMode)
        } else {
            setOptionsConfigurationMode([
                {
                    label: TrajectoryConfigurationMode.FilterTool,
                    id: TrajectoryConfigurationMode.FilterTool
                }
            ])
            stationContext.setConfigurationMode(TrajectoryConfigurationMode.FilterTool as TrajectoryConfigurationMode)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stationContext.deviceData?.uuid])

    useEffect(() => {
        if (
            trajectoryConfigurationContext.data.polygonData?.points &&
            trajectoryConfigurationContext.data.polygonData.points.length !== 0
        ) {
            setpolygonExists(true)
        } else {
            setpolygonExists(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trajectoryConfigurationContext.data])

    useEffect(() => {
        const current_filter = trajectoryConfigurationContext.currentFilter?.toString()

        const updatedFilterToolOptionsViewMode = trajectoryConfigurationContext.filterData
            .map((_options: any, index: { toString: () => any }) => ({
                id: index.toString(),
                label: `Filter ${index.toString()}`
            }))
            .filter((_options: any, index: { toString: () => any }) => index.toString() !== current_filter)

        if (polygonExists) {
            updatedFilterToolOptionsViewMode.push({
                id: 'polygon',
                label: 'polygon'
            })
        }

        const inlineToolOptionsViewMode = trajectoryConfigurationContext.filterData.map(
            (_options: any, index: { toString: () => any }) => ({
                id: index.toString(),
                label: `Filter ${index.toString()}`
            })
        )
        if (
            !arraysAreEqual(optionsViewMode.FilterTool, updatedFilterToolOptionsViewMode) ||
            !arraysAreEqual(optionsViewMode.InlineTool, inlineToolOptionsViewMode)
        ) {
            setOptionsViewMode({
                FilterTool: updatedFilterToolOptionsViewMode,
                InlineTool: inlineToolOptionsViewMode
            })
            trajectoryConfigurationContext.setViewMode([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        trajectoryConfigurationContext.filterData,
        trajectoryConfigurationContext.currentFilter,
        polygonExists,
        optionsViewMode
    ])

    useEffect(() => {
        trajectoryConfigurationContext.setViewMode([])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stationContext.configurationMode])

    const validation_polygon = () => {
        if (!trajectoryConfigurationContext.data.polygonData?.lines) {
            return {
                valid: false,
                description: 'No polygon data available to validate.'
            }
        }
        const validation = validate_polygon(
            trajectoryConfigurationContext.data.polygonData.lines,
            trajectoryConfigurationContext.data.enableEventClassificationData.value
        )
        if (!validation.valid) {
            trajectoryConfigurationContext.setValidationModalStatePolygon(true, validation.description)
        }
        return validation
    }

    const export_inline_tool_data = () => {
        const diff_config_submit = JSON.parse(JSON.stringify(stationContext.deviceData?.config.diff))
        if (!trajectoryConfigurationContext.data.polygonData?.lines?.length) {
            delete_recursive(diff_config_submit, 'vehicle_detection.scanner.trajectory_classification.extrapolation')
        } else {
            insert_recursive(
                diff_config_submit,
                'vehicle_detection.scanner.trajectory_classification.extrapolation.frame.frame_info',
                export_transformation_inline_tool(
                    trajectoryConfigurationContext.data.polygonData.lines,
                    trajectoryConfigurationContext.imageSize.width,
                    trajectoryConfigurationContext.imageSize.height
                )
            )
        }
        if (trajectoryConfigurationContext.data.enableEventClassificationData.base === true) {
            delete_recursive(diff_config_submit, 'vehicle_detection.scanner.trajectory_classification.classifier')
        }
        if (trajectoryConfigurationContext.data.enableEventClassificationData.base === false) {
            insert_recursive(
                diff_config_submit,
                'vehicle_detection.scanner.trajectory_classification.classifier',
                trajectoryConfigurationContext.data.enableEventClassificationData.value
            )
        }
        return diff_config_submit
    }

    const eventClassificationState =
        trajectoryConfigurationContext?.data?.enableEventClassificationData?.base === 'true' ||
        trajectoryConfigurationContext?.data?.enableEventClassificationData?.base === true
            ? {
                  label: 'OFF (Default)',
                  id: 'default'
              }
            : trajectoryConfigurationContext?.data.enableEventClassificationData?.value === 'extrapolation'
              ? {
                    label: 'ON',
                    id: 'extrapolation'
                }
              : trajectoryConfigurationContext?.data?.enableEventClassificationData?.value === 'legacy_classification'
                ? {
                      label: 'OFF',
                      id: 'legacy_classification'
                  }
                : null

    return (
        <div>
            <WemoloAccordion title="Vehicle Trajectory Tools" defaultExpanded={true}>
                <OverlaySpinner
                    loading={
                        trajectoryConfigurationContext.loadingFetchSystemImage ||
                        stationContext.loadingDeviceConfigSave ||
                        stationContext.loadingSaveStationConfigFilter
                    }
                >
                    <div className={styles.row}>
                        <div className={styles.configCol}>
                            <InputWrapper label="Configuration Mode">
                                <WemoloDropdown
                                    size="medium"
                                    placeholder="Select Mode"
                                    options={toMenuOptions(optionsConfigurationMode || [])}
                                    value={stationContext.configurationMode ?? undefined}
                                    onChange={(id) => {
                                        if (id) {
                                            Sentry.logger.info('vehicle_trajectory.configuration_mode_changed', {
                                                event: 'vehicle_trajectory_configuration_mode_changed',
                                                mode: id,
                                                deviceUuid: stationContext.deviceData?.uuid,
                                                userId: getUserUuidFromLocalStorage()
                                            })
                                            stationContext.setConfigurationMode(id as TrajectoryConfigurationMode)
                                        }
                                    }}
                                />
                            </InputWrapper>
                        </div>
                        <div className={styles.viewModeCol}>
                            <InputWrapper label="View Mode">
                                {(() => {
                                    const currentViewModeOptions: Array<{ id: string; label: string }> =
                                        stationContext.configurationMode === TrajectoryConfigurationMode.InlineTool
                                            ? optionsViewMode.InlineTool
                                            : optionsViewMode.FilterTool
                                    return (
                                        <WemoloDropdown
                                            size="medium"
                                            allowMultiple
                                            placeholder="Select Mode"
                                            options={toMenuOptions(currentViewModeOptions)}
                                            value={trajectoryConfigurationContext.viewMode.map(
                                                (v: Option) => v.id as string
                                            )}
                                            onChange={(ids) => {
                                                const idArray = ids ?? []
                                                const selected = idArray
                                                    .map((id) => currentViewModeOptions.find((o) => o.id === id))
                                                    .filter((o): o is { id: string; label: string } => Boolean(o))
                                                Sentry.logger.info('vehicle_trajectory.view_mode_changed', {
                                                    event: 'vehicle_trajectory_view_mode_changed',
                                                    configurationMode: stationContext.configurationMode,
                                                    selectedIds: selected.map((o) => o.id),
                                                    selectedCount: selected.length,
                                                    deviceUuid: stationContext.deviceData?.uuid,
                                                    userId: getUserUuidFromLocalStorage()
                                                })
                                                trajectoryConfigurationContext.setViewMode(selected as Option[])
                                            }}
                                        />
                                    )
                                })()}
                            </InputWrapper>
                        </div>
                    </div>
                    <div className={styles.row}>
                        <div className={styles.imageSourceCol}>
                            <InputWrapper label="Image Source">
                                <ImageSelectionButtonGroup
                                    loadLiveViewScreenshotHandler={
                                        trajectoryConfigurationContext.loadLiveViewScreenshotHandler
                                    }
                                    removeLiveViewUrl={trajectoryConfigurationContext.removeLiveViewUrl}
                                    setLatestImage={trajectoryConfigurationContext.setLatestImage}
                                />
                            </InputWrapper>
                        </div>

                        <div className={styles.spacer}></div>
                        <div className={styles.actionsCol}>
                            {stationContext.configurationMode === TrajectoryConfigurationMode.InlineTool ? (
                                <>
                                    <WemoloButton
                                        view="primary"
                                        size="small"
                                        className={styles.saveButton}
                                        disabled={!hasChanges || stationContext.loadingDeviceConfigSave}
                                        loading={stationContext.loadingDeviceConfigSave}
                                        onClick={() => {
                                            if (eventClassificationState?.id !== 'extrapolation') {
                                                if (
                                                    !window.confirm(
                                                        'Event Classification is disabled. Are you sure you want to continue?'
                                                    )
                                                ) {
                                                    return
                                                }
                                            }
                                            const valid_poly = validation_polygon()

                                            if (valid_poly.valid === true) {
                                                stationContext.saveDiffConfig(export_inline_tool_data())
                                                setFormChanges(false)
                                            } else {
                                                return
                                            }
                                        }}
                                    >
                                        Save Inline Tool Config
                                    </WemoloButton>
                                    <InfoTooltip content={forceUpdateDisabled ? 'No changes to force update' : null}>
                                        <WemoloButton
                                            view="destructive"
                                            size="small"
                                            disabled={
                                                forceUpdateDisabled || trajectoryConfigurationContext.loadingForceUpdate
                                            }
                                            loading={trajectoryConfigurationContext.loadingForceUpdate}
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        'Are you sure you want to force update the configuration for this device?'
                                                    )
                                                ) {
                                                    if (stationContext.deviceData?.uuid) {
                                                        trajectoryConfigurationContext
                                                            .setDeviceForceUpdate({
                                                                device_uuid: stationContext.deviceData.uuid
                                                            })
                                                            .catch(() => {})
                                                    } else {
                                                        alert('No device data available')
                                                    }
                                                }
                                            }}
                                        >
                                            Force Update
                                        </WemoloButton>
                                    </InfoTooltip>
                                </>
                            ) : stationContext.configurationMode === TrajectoryConfigurationMode.FilterTool ? (
                                <WemoloButton
                                    view="primary"
                                    size="small"
                                    className={styles.saveButton}
                                    onClick={() => {
                                        stationContext.saveStationConfigFilter(
                                            trajectoryConfigurationContext.filterData
                                        )
                                        setFormChanges(false)
                                    }}
                                >
                                    Save Filter Tool Config
                                </WemoloButton>
                            ) : null}
                        </div>
                    </div>
                    <div className={styles.toolsRow}>
                        <div>
                            {stationContext.configurationMode === TrajectoryConfigurationMode.InlineTool ? (
                                <div>
                                    <OverlaySpinner loading={trajectoryConfigurationContext.loading}>
                                        <InlineTool></InlineTool>
                                    </OverlaySpinner>

                                    <FormSavePrompt />
                                </div>
                            ) : stationContext.configurationMode === TrajectoryConfigurationMode.FilterTool ? (
                                <div>
                                    <OverlaySpinner loading={trajectoryConfigurationContext.loading}>
                                        <FilterTool></FilterTool>
                                    </OverlaySpinner>

                                    <FormSavePrompt />
                                </div>
                            ) : null}
                        </div>
                        <div className={styles.sidePanel}>
                            <StationTrajectoryPanel>
                                <div className={styles.controls}>
                                    <WemoloTypography variant="body-medium" tag="div" className={styles.sectionLabel}>
                                        Trajectory View Configuration
                                    </WemoloTypography>
                                    <WemoloDropdown
                                        size="medium"
                                        options={[
                                            { label: 'ON', value: 'ON' },
                                            { label: 'OFF', value: 'OFF' }
                                        ]}
                                        value={
                                            trajectoryConfigurationContext.enableTrajectoryView === true
                                                ? 'ON'
                                                : trajectoryConfigurationContext.enableTrajectoryView === false
                                                  ? 'OFF'
                                                  : undefined
                                        }
                                        onChange={(id) => {
                                            if (id === 'ON' || id === 'OFF') {
                                                Sentry.logger.info('vehicle_trajectory.enable_toggled', {
                                                    event: 'vehicle_trajectory_enable_toggled',
                                                    enabled: id === 'ON',
                                                    deviceUuid: stationContext.deviceData?.uuid,
                                                    userId: getUserUuidFromLocalStorage()
                                                })
                                            }
                                            if (id === 'ON')
                                                trajectoryConfigurationContext.setEnableTrajectoryView(true)
                                            else if (id === 'OFF')
                                                trajectoryConfigurationContext.setEnableTrajectoryView(false)
                                        }}
                                    />
                                    <div className={styles.directionRow}>
                                        <div className={styles.directionItem}>
                                            <WemoloTypography
                                                variant="body-medium"
                                                tag="div"
                                                className={styles.sectionLabel}
                                            >
                                                Direction
                                            </WemoloTypography>
                                            <WemoloDropdown
                                                size="medium"
                                                placeholder="Select Mode"
                                                options={[
                                                    { label: 'IN', value: 'IN' },
                                                    { label: 'OUT', value: 'OUT' },
                                                    { label: 'TURN', value: 'TURN' },
                                                    { label: 'INV', value: 'INV' },
                                                    { label: 'UNK', value: 'UNK' }
                                                ]}
                                                value={
                                                    (transformValue(
                                                        trajectoryConfigurationContext.trajectoryViewDirection
                                                    )?.id as string) ?? undefined
                                                }
                                                onChange={(id) => {
                                                    if (id) {
                                                        Sentry.logger.info('vehicle_trajectory.direction_changed', {
                                                            event: 'vehicle_trajectory_direction_changed',
                                                            direction: id,
                                                            deviceUuid: stationContext.deviceData?.uuid,
                                                            userId: getUserUuidFromLocalStorage()
                                                        })
                                                        trajectoryConfigurationContext.setTrajectoryViewDirection(id)
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className={styles.directionSpacer}></div>
                                        <div className={styles.directionItem}>
                                            <WemoloTypography
                                                variant="body-medium"
                                                tag="div"
                                                className={styles.sectionLabel}
                                            >
                                                Missing LP
                                            </WemoloTypography>
                                            <WemoloDropdown
                                                size="medium"
                                                placeholder="Select Mode"
                                                options={[
                                                    { label: 'TRUE', value: 'true' },
                                                    { label: 'FALSE', value: 'false' }
                                                ]}
                                                value={
                                                    (transformValue(
                                                        trajectoryConfigurationContext.trajectoryViewMissingLP
                                                    )?.id as string) ?? undefined
                                                }
                                                onChange={(id) => {
                                                    if (id) {
                                                        Sentry.logger.info('vehicle_trajectory.missing_lp_changed', {
                                                            event: 'vehicle_trajectory_missing_lp_changed',
                                                            missingLp: id === 'true',
                                                            deviceUuid: stationContext.deviceData?.uuid,
                                                            userId: getUserUuidFromLocalStorage()
                                                        })
                                                        trajectoryConfigurationContext.setCoordinasetTrajectoryViewMissingLP(
                                                            id === 'true'
                                                        )
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <WemoloTypography variant="body-medium" tag="div" className={styles.sectionLabel}>
                                        Trajectories: Vehicle
                                    </WemoloTypography>
                                    {trajectoryConfigurationContext.vehicleDetectionsTrajectoryView
                                        .available_trajectories === 0 ? (
                                        <WemoloTypography
                                            variant="body-small"
                                            tag="div"
                                            className={styles.emptyTrajectories}
                                        >
                                            No Vehicle Trajectories Available
                                        </WemoloTypography>
                                    ) : (
                                        <SliderComponent
                                            max={
                                                trajectoryConfigurationContext.vehicleDetectionsTrajectoryView
                                                    .available_trajectories
                                            }
                                            setValue={
                                                trajectoryConfigurationContext.setVehicleDetectionsTrajectoryView as any
                                            }
                                            value_view={
                                                trajectoryConfigurationContext.vehicleDetectionsTrajectoryView
                                                    .view_trajectories
                                            }
                                        />
                                    )}
                                    <WemoloTypography variant="body-medium" tag="div" className={styles.lpLabel}>
                                        Trajectories: LP
                                    </WemoloTypography>
                                    {trajectoryConfigurationContext.lpDetectionsTrajectoryView
                                        .available_trajectories === 0 ? (
                                        <WemoloTypography
                                            variant="body-small"
                                            tag="div"
                                            className={styles.emptyTrajectories}
                                        >
                                            No LP Trajectories Available
                                        </WemoloTypography>
                                    ) : (
                                        <SliderComponent
                                            max={
                                                trajectoryConfigurationContext.lpDetectionsTrajectoryView
                                                    .available_trajectories
                                            }
                                            setValue={
                                                trajectoryConfigurationContext.setLpDetectionsTrajectoryView as any
                                            }
                                            value_view={
                                                trajectoryConfigurationContext.lpDetectionsTrajectoryView
                                                    .view_trajectories
                                            }
                                        />
                                    )}
                                    <WemoloTypography variant="body-medium" tag="div" className={styles.sectionLabel}>
                                        Best Detection
                                    </WemoloTypography>
                                    <WemoloDropdown
                                        size="medium"
                                        menuPlacement="top"
                                        placeholder="Select Mode"
                                        options={[
                                            { label: 'SHOW', value: '1' },
                                            { label: 'DONT SHOW', value: '0' }
                                        ]}
                                        value={trajectoryConfigurationContext.bestDetection === 1 ? '1' : '0'}
                                        onChange={(id) => {
                                            if (id) {
                                                Sentry.logger.info('vehicle_trajectory.best_detection_changed', {
                                                    event: 'vehicle_trajectory_best_detection_changed',
                                                    bestDetection: Number(id),
                                                    deviceUuid: stationContext.deviceData?.uuid,
                                                    userId: getUserUuidFromLocalStorage()
                                                })
                                                trajectoryConfigurationContext.setBestDetection(Number(id))
                                            }
                                        }}
                                    />
                                </div>
                            </StationTrajectoryPanel>
                        </div>
                    </div>
                </OverlaySpinner>
            </WemoloAccordion>
        </div>
    )
}
